import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import ThemeInterface from '@/theme-interface'

// FLAG:useDocuAi — invoice-review only: the line description (item's name) used to be
// plain text, so a wrong or truncated name could not be corrected during review.
// One click turns it into a field, clicking outside (or Enter) commits and renders the
// text again, Escape restores the previous value. The edited text rides on the position
// and is persisted by the normal review save (documentInvoiceDetails.positions).
// Never rendered on the legacy expense/revenue path — the list returns before this.
const useStyles = makeStyles((theme: ThemeInterface) => ({
  text: {
    cursor: 'text',
    borderRadius: 3,
    // negative margin keeps the padded hover box from shifting the text
    padding: '0 3px',
    margin: '0 -3px',
    '&:hover': {
      background: theme.backgroundColor,
      boxShadow: `inset 0 -1px 0 ${theme.borderColor}`,
    },
  },
  readOnly: {
    cursor: 'default',
    padding: 0,
    margin: 0,
    '&:hover': {
      background: 'none',
      boxShadow: 'none',
    },
  },
  input: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    // the field grows with its content (see the layout effect) instead of scrolling
    resize: 'none',
    overflow: 'hidden',
    // inherit so the field looks exactly like the text it replaces
    font: 'inherit',
    lineHeight: 'inherit',
    color: theme.textColor,
    fontStyle: 'normal',
    background: theme.white,
    border: `1px solid ${theme.primary}`,
    borderRadius: 3,
    padding: '1px 3px',
    margin: '0 -4px',
  },
}))

interface PositionDescriptionEditProps {
  value?: string | null
  // shown (and edited from empty) when the position carries no description
  placeholder: string
  // XML e-invoice lines are legally binding: the server keeps its own description
  // on save (xmlLockedFields), so those must not offer an edit at all
  readOnly?: boolean
  disabled?: boolean
  title?: string
  ariaLabel: string
  dataQsId?: string
  onChange: (description: string) => void
}

export const PositionDescriptionEdit = (props: PositionDescriptionEditProps) => {
  const { value, placeholder, readOnly, disabled, title, ariaLabel, dataQsId, onChange } = props
  const classes = useStyles()
  // null = display mode; a string is the uncommitted draft
  const [draft, setDraft] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  // Escape unmounts the field, and a removed element does not reliably fire blur —
  // flag the cancel so a late blur cannot commit the discarded draft anyway.
  const cancelledRef = useRef(false)
  const isEditing = draft !== null
  const editable = !readOnly && !disabled

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.style.height = 'auto'
    input.style.height = `${input.scrollHeight}px`
  }, [draft])

  useEffect(() => {
    const input = inputRef.current
    if (!input) return
    input.focus()
    // caret at the end — a select-all would make the first keystroke wipe the name
    input.setSelectionRange(input.value.length, input.value.length)
  }, [isEditing])

  const startEdit = () => {
    if (!editable) return
    cancelledRef.current = false
    setDraft(value || '')
  }

  const commit = () => {
    const next = draft || ''
    setDraft(null)
    if (next !== (value || '')) { onChange(next) }
  }

  const cancel = () => {
    cancelledRef.current = true
    setDraft(null)
  }

  const handleBlur = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }
    commit()
  }

  // stopPropagation: the review runs inside a drawer that closes on Escape, and the
  // surrounding form must not see these keys either.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      commit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      cancel()
    }
  }

  if (isEditing) {
    return (
      <textarea
        ref={inputRef}
        className={classes.input}
        rows={1}
        value={draft || ''}
        aria-label={ariaLabel}
        data-qs-id={dataQsId}
        onChange={event => setDraft(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    )
  }

  return (
    <span
      className={clsx(classes.text, !editable && classes.readOnly)}
      title={title}
      // the description text itself is the accessible name — `ariaLabel` names the
      // field once it is open, so the two never compete for the same label
      role={editable ? 'button' : undefined}
      tabIndex={editable ? 0 : undefined}
      data-qs-id={dataQsId}
      onClick={startEdit}
      onKeyDown={event => {
        if (!editable) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          startEdit()
        }
      }}
    >
      {value || placeholder}
    </span>
  )
}

export default PositionDescriptionEdit
